import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import styled from 'styled-components';

import BalancesHistoryPage from '#/components/balances/history/BalancesHistoryPage';
import { useRouterLocation } from '#/components/hooks';
import OpenOrdersPage from '#/components/orders/open/OpenOrdersPage';
import FundingHistoryPage from '#/components/positions/funding/FundingHistoryPage';
import PositionsPage from '#/components/positions/PositionsPage';
import TradeHistoryPage from '#/components/trading/TradeHistoryPage';
import VaultManagerWithdrawalQueueWalletPage from '#/components/vaults/components/VaultManagerWithdrawalQueueWalletPage';
import VaultStatsPanel from '#/components/vaults/components/VaultStatsPanel';
import { useOwnVaultQuery } from '#/components/vaults/shared/utils';
import WalletAuthorizationsPage from '#/components/wallet/authorizations/WalletAuthorizationsPage';
import { useSelector } from '#/reduxState/store';

import WalletDashboardMenu from './WalletDashboardMenu';
import WalletDashboardMenuPageMobile from './WalletDashboardMenuPageMobile';

const AppRoutesUser = lazy(() => import('#/components/AppRoutesUser'));

const Wrap = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 4px 6px;
  padding: 4px;
`;

const Sidebar = styled.div`
  border-radius: 2px;
  background: var(--elevation-light);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Main = styled.div`
  overflow: hidden;
  .isMobileStyle & {
    height: 100%;
  }
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const VaultStatsPanelWrap = styled.div`
  padding: 0 8px;
`;

/**
 * To make navigation better, we want to remember last visited page
 * and on next "/wallet" visit, user will jump to specific sub-page
 */
let globalLastVisitPath = 'positions';

function usePersistLastVisitedWalletPath(pathName: string) {
  useEffect(() => {
    // Whitelist which should be persisted
    // so we dont persist some modals
    if (
      ![
        '/wallet/positions',
        '/wallet/open-orders',
        '/wallet/history/trades',
        '/wallet/history/deposits-withdrawals',
        '/wallet/history/funding',
        '/wallet/api-keys',
        '',
      ].includes(pathName)
    ) {
      return;
    }
    globalLastVisitPath = pathName;
  }, [pathName]);
}

function WalletDashboardPage() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const { pathname } = useRouterLocation();

  usePersistLastVisitedWalletPath(pathname);

  const ownVault = useOwnVaultQuery(!isMobile);

  const routes = useMemo(
    () => (
      <Suspense>
        <Routes>
          <Route
            path="menu-mobile"
            element={<WalletDashboardMenuPageMobile />}
          />

          <Route path="positions" element={<PositionsPage />} />
          <Route path="open-orders" element={<OpenOrdersPage />} />
          <Route path="open-orders/:orderUuid" element={<OpenOrdersPage />} />

          <Route
            path="history"
            element={<Navigate replace to="/wallet/history/trades" />}
          />
          <Route path="history/trades" element={<TradeHistoryPage />} />
          <Route
            path="history/trades/:orderUuid"
            element={<TradeHistoryPage />}
          />
          <Route
            path="history/trades/:orderUuid/:fillId"
            element={<TradeHistoryPage />}
          />
          <Route
            path="history/deposits-withdrawals"
            element={<BalancesHistoryPage />}
          />
          <Route
            path="history/vault-withdrawal-queue"
            element={<VaultManagerWithdrawalQueueWalletPage />}
          />
          <Route path="history/funding" element={<FundingHistoryPage />} />

          <Route path="api-keys">
            <Route index path="*" element={<AppRoutesUser />} />
          </Route>

          <Route path="session-keys" element={<WalletAuthorizationsPage />} />
        </Routes>
      </Suspense>
    ),
    []
  );

  if (pathname === '/wallet') {
    return <Navigate replace to={globalLastVisitPath} />;
  }

  if (isMobile) {
    return <Main>{routes}</Main>;
  }
  return (
    <Wrap>
      <Sidebar>
        <WalletDashboardMenu />
        {ownVault && (
          <VaultStatsPanelWrap>
            <VaultStatsPanel managerWallet={ownVault.manager} />
          </VaultStatsPanelWrap>
        )}
      </Sidebar>
      <Main>{routes}</Main>
    </Wrap>
  );
}

export default WalletDashboardPage;
